import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import { ITask, TasksListResponse, TaskFilterOptions, TaskStats } from './types/task';
import { TaskCard, TasksList } from './components/tasks';
import TaskEditModal from './components/tasks/TaskEditModal';
import './TasksView.css';

const TasksView: React.FC = () => {
    const { token, user } = useAuth();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    // URL state
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const filterGroupParam = searchParams.get('group') || '';
    const filterGroups = filterGroupParam ? filterGroupParam.split(',').filter(g => g) : [];
    const filterStatus = searchParams.get('status') || '';
    const viewMode = (searchParams.get('view') as 'grid' | 'list') || 'grid';
    const searchQuery = searchParams.get('search') || '';

    // Local state
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalPages, setTotalPages] = useState(1);
    const [totalTasks, setTotalTasks] = useState(0);
    const [filterOptions, setFilterOptions] = useState<TaskFilterOptions>({
        groups: [],
        providers: [],
        statuses: ['ACTIVE', 'INACTIVE']
    });
    const [stats, setStats] = useState<TaskStats | null>(null);
    const [editingTask, setEditingTask] = useState<ITask | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [experimentCountsByGroup, setExperimentCountsByGroup] = useState<Record<string, number>>({});

    const apiBaseUrl = env.apiUrl;
    const apiKey = env.apiKey;

    // Update URL params helper
    const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
        const newParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, value);
            }
        });
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Fetch filter options
    const fetchFilterOptions = useCallback(async () => {
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/tasks/filter-options`, {
                headers
            });

            if (response.ok) {
                const data = await response.json();
                setFilterOptions(data);
            }
        } catch (err) {
            console.error('Failed to fetch filter options:', err);
        }
    }, [token, apiBaseUrl, apiKey]);

    // Fetch stats
    const fetchStats = useCallback(async () => {
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/tasks/stats`, {
                headers
            });

            if (response.ok) {
                const data = await response.json();
                setStats(data);
            }
        } catch (err) {
            console.error('Failed to fetch stats:', err);
        }
    }, [token, apiBaseUrl, apiKey]);

    // Fetch experiment counts by group
    const fetchExperimentCounts = useCallback(async () => {
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/tasks/experiments/by-group`, {
                headers
            });

            if (response.ok) {
                const data = await response.json();
                // byGroup is already a Record<string, number> from the backend
                if (data.byGroup && typeof data.byGroup === 'object') {
                    setExperimentCountsByGroup(data.byGroup);
                }
            }
        } catch (err) {
            console.error('Failed to fetch experiment counts:', err);
        }
    }, [token, apiBaseUrl, apiKey]);

    // Fetch tasks
    const fetchTasks = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: '20'
            });
            // Support multiple groups (comma-separated)
            if (filterGroups.length > 0) params.append('group', filterGroups.join(','));
            if (filterStatus) params.append('status', filterStatus);
            if (searchQuery) params.append('search', searchQuery);

            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/tasks?${params}`, {
                headers
            });

            if (!response.ok) {
                const errText = await response.text();
                throw new Error('Failed to fetch tasks: ' + (response.statusText || errText || 'Unknown error'));
            }

            const data: TasksListResponse = await response.json();
            setTasks(data.tasks);
            setTotalPages(data.pagination.totalPages);
            setTotalTasks(data.pagination.total);
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [currentPage, filterGroups.join(','), filterStatus, searchQuery, token, apiBaseUrl, apiKey]);

    // Toggle task status
    const handleToggleStatus = async (taskId: string) => {
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/tasks/${taskId}/toggle-status`, {
                method: 'POST',
                headers
            });

            if (!response.ok) {
                throw new Error('Failed to toggle task status');
            }

            const data = await response.json();

            // Update the task in the list
            setTasks(prevTasks =>
                prevTasks.map(task =>
                    task._id === taskId ? data.task : task
                )
            );

            // Refresh stats
            fetchStats();
        } catch (err) {
            console.error('Failed to toggle task status:', err);
            setError(t('tasks.toggleError'));
        }
    };

    // Set all tasks in a group to a specific status
    const handleGroupStatusChange = async (group: string, status: 'ACTIVE' | 'INACTIVE') => {
        try {
            const headers: Record<string, string> = {
                'x-api-key': apiKey,
                'Content-Type': 'application/json'
            };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiBaseUrl}/admin/tasks/group-status`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ group, status })
            });

            if (!response.ok) {
                throw new Error('Failed to update group status');
            }

            // Refresh tasks and stats
            fetchTasks();
            fetchStats();
        } catch (err) {
            console.error('Failed to update group status:', err);
            setError(t('tasks.groupStatusError'));
        }
    };

    // Open edit modal for a task
    const handleEdit = (task: ITask) => {
        setEditingTask(task);
        setIsEditModalOpen(true);
    };

    // Save task updates
    const handleSaveTask = async (updates: Partial<ITask>) => {
        if (!editingTask) return;

        const headers: Record<string, string> = {
            'x-api-key': apiKey,
            'Content-Type': 'application/json'
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${apiBaseUrl}/admin/tasks/${editingTask._id}`, {
            method: 'PUT',
            headers,
            body: JSON.stringify(updates)
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update task');
        }

        const data = await response.json();

        // Update the task in the list
        setTasks(prevTasks =>
            prevTasks.map(task =>
                task._id === editingTask._id ? data.task : task
            )
        );

        // Close modal
        setIsEditModalOpen(false);
        setEditingTask(null);
    };

    // Initial load
    useEffect(() => {
        fetchFilterOptions();
        fetchStats();
        fetchExperimentCounts();
    }, [fetchFilterOptions, fetchStats, fetchExperimentCounts]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const handlePageChange = (page: number) => {
        updateSearchParams({ page: page.toString() });
    };

    // Toggle a group in the filter (for multi-select)
    const handleGroupToggle = (group: string) => {
        const newGroups = filterGroups.includes(group)
            ? filterGroups.filter(g => g !== group)
            : [...filterGroups, group];
        updateSearchParams({
            group: newGroups.length > 0 ? newGroups.join(',') : null,
            page: '1'
        });
    };

    // Set filter to a single group (from dropdown)
    const handleGroupChange = (group: string) => {
        updateSearchParams({ group: group || null, page: '1' });
    };

    const handleStatusChange = (status: string) => {
        updateSearchParams({ status: status || null, page: '1' });
    };

    const handleViewModeChange = (mode: 'grid' | 'list') => {
        updateSearchParams({ view: mode });
    };

    const handleClearFilters = () => {
        updateSearchParams({
            group: null,
            status: null,
            search: null,
            page: '1'
        });
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateSearchParams({ search: value || null, page: '1' });
    };

    const hasActiveFilters = filterGroups.length > 0 || filterStatus || searchQuery;

    // Check admin access
    if (user?.role !== 'ADMIN') {
        return (
            <div className="tasks-view">
                <div className="access-denied">
                    <span className="material-icons">lock</span>
                    <h2>{t('tasks.accessDenied')}</h2>
                    <p>{t('tasks.adminOnly')}</p>
                </div>
            </div>
        );
    }

    if (loading && tasks.length === 0) {
        return (
            <div className="tasks-view">
                <div className="loading-container">
                    <div className="tasks-loader-flex">
                        <span className="material-icons loader-icon rotating">sync</span>
                        <span className="loader-text">{t('loading.tasks')}</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="tasks-view">
            {/* Header */}
            <div className="tasks-header">
                <div className="header-title-section">
                    <h1>{t('tasks.title')}</h1>
                    <span className="tasks-count">
                        {totalTasks} {t('tasks.tasksCount')}
                    </span>
                </div>
                {stats && (
                    <div className="tasks-stats">
                        <div className="stat-item active">
                            <span className="material-icons">play_circle</span>
                            <span>{stats.active} {t('tasks.active')}</span>
                        </div>
                        <div className="stat-item inactive">
                            <span className="material-icons">pause_circle</span>
                            <span>{stats.inactive} {t('tasks.inactive')}</span>
                        </div>
                        {stats.recentlyRun > 0 && (
                            <div className="stat-item recent">
                                <span className="material-icons">schedule</span>
                                <span>{stats.recentlyRun} {t('tasks.recentlyRun')}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Group Control Panel */}
            {filterOptions.groups.length > 0 && (
                <div className="group-control-panel">
                    <div className="group-control-header">
                        <span className="material-icons">category</span>
                        <span>{t('tasks.groupControl.title')}</span>
                        {filterGroups.length > 0 && (
                            <button
                                className="group-clear-filter-btn"
                                onClick={() => updateSearchParams({ group: null, page: '1' })}
                                title={t('tasks.groupControl.clearFilter')}
                            >
                                <span className="material-icons">filter_alt_off</span>
                                <span>{t('tasks.groupControl.clearFilter')}</span>
                            </button>
                        )}
                    </div>
                    <div className="group-control-list">
                        {filterOptions.groups.map((group) => {
                            const groupStats = stats?.byGroup?.find(g => g.group === group);
                            const isSelected = filterGroups.includes(group);
                            return (
                                <div
                                    key={group}
                                    className={`group-control-item ${isSelected ? 'selected' : ''}`}
                                >
                                    <button
                                        className="group-control-filter-btn"
                                        onClick={() => handleGroupToggle(group)}
                                        title={isSelected
                                            ? t('tasks.groupControl.removeFilter', { group })
                                            : t('tasks.groupControl.filterBy', { group })
                                        }
                                    >
                                        <span className="material-icons group-filter-icon">
                                            {isSelected ? 'check_box' : 'check_box_outline_blank'}
                                        </span>
                                        <span className="group-control-name">{group}</span>
                                    </button>
                                    <div className="group-control-counts">
                                        {groupStats && (
                                            <span className="group-control-count tasks-count">
                                                {groupStats.count} {t('tasks.tasksCount')}
                                            </span>
                                        )}
                                        {experimentCountsByGroup[group] !== undefined && experimentCountsByGroup[group] > 0 && (
                                            <span className="group-control-count experiments-count" title={t('tasks.experimentsCount')}>
                                                <span className="material-icons experiment-icon">science</span>
                                                {experimentCountsByGroup[group]}
                                            </span>
                                        )}
                                    </div>
                                    <div className="group-control-buttons">
                                        <button
                                            className="group-ctrl-btn activate"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleGroupStatusChange(group, 'ACTIVE');
                                            }}
                                            title={t('tasks.groupActions.activateAllTitle', { group })}
                                        >
                                            <span className="material-icons">play_arrow</span>
                                        </button>
                                        <button
                                            className="group-ctrl-btn deactivate"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleGroupStatusChange(group, 'INACTIVE');
                                            }}
                                            title={t('tasks.groupActions.deactivateAllTitle', { group })}
                                        >
                                            <span className="material-icons">pause</span>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Search and Controls Bar */}
            <div className="tasks-controls">
                <div className="search-section">
                    <div className="search-input-wrapper">
                        <span className="material-icons search-icon">search</span>
                        <input
                            type="text"
                            className="search-input"
                            placeholder={t('tasks.searchPlaceholder')}
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />
                        {searchQuery && (
                            <button
                                className="search-clear-btn"
                                onClick={() => updateSearchParams({ search: null, page: '1' })}
                            >
                                <span className="material-icons">close</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="view-toggle">
                    <button
                        className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('grid')}
                        title={t('tasks.viewMode.grid')}
                    >
                        <span className="material-icons">grid_view</span>
                    </button>
                    <button
                        className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                        onClick={() => handleViewModeChange('list')}
                        title={t('tasks.viewMode.list')}
                    >
                        <span className="material-icons">view_list</span>
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="tasks-filters">
                <div className="filters-row">
                    <select
                        className="filter-select"
                        value={filterGroups.length === 1 ? filterGroups[0] : ''}
                        onChange={(e) => handleGroupChange(e.target.value)}
                    >
                        <option value="">
                            {filterGroups.length > 1
                                ? t('tasks.filters.multipleGroups', { count: filterGroups.length })
                                : t('tasks.filters.allGroups')
                            }
                        </option>
                        {filterOptions.groups.map((group) => (
                            <option key={group} value={group}>{group}</option>
                        ))}
                    </select>

                    {/* Group action buttons - shown when groups are selected */}
                    {filterGroups.length > 0 && (
                        <div className="group-actions">
                            {filterGroups.map(group => (
                                <button
                                    key={group}
                                    className="group-action-btn activate"
                                    onClick={() => handleGroupStatusChange(group, 'ACTIVE')}
                                    title={t('tasks.groupActions.activateAllTitle', { group })}
                                >
                                    <span className="material-icons">play_circle</span>
                                    {filterGroups.length === 1
                                        ? t('tasks.groupActions.activateAll')
                                        : group
                                    }
                                </button>
                            ))}
                            {filterGroups.map(group => (
                                <button
                                    key={group}
                                    className="group-action-btn deactivate"
                                    onClick={() => handleGroupStatusChange(group, 'INACTIVE')}
                                    title={t('tasks.groupActions.deactivateAllTitle', { group })}
                                >
                                    <span className="material-icons">pause_circle</span>
                                    {filterGroups.length === 1
                                        ? t('tasks.groupActions.deactivateAll')
                                        : group
                                    }
                                </button>
                            ))}
                        </div>
                    )}

                    <select
                        className="filter-select"
                        value={filterStatus}
                        onChange={(e) => handleStatusChange(e.target.value)}
                    >
                        <option value="">{t('tasks.filters.allStatuses')}</option>
                        {filterOptions.statuses.map((status) => (
                            <option key={status} value={status}>{status}</option>
                        ))}
                    </select>

                    {hasActiveFilters && (
                        <button className="clear-filters-btn" onClick={handleClearFilters}>
                            <span className="material-icons">clear_all</span>
                            {t('tasks.filters.clearAll')}
                        </button>
                    )}
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="error-banner">
                    <span className="material-icons">error</span>
                    <span>{error}</span>
                    <button onClick={fetchTasks} className="retry-btn">
                        <span className="material-icons">refresh</span>
                        {t('buttons.retry')}
                    </button>
                </div>
            )}

            {/* Results */}
            {tasks.length === 0 && !loading ? (
                <div className="empty-state">
                    <span className="material-icons">assignment</span>
                    <h3>
                        {hasActiveFilters
                            ? t('tasks.noDataFiltered')
                            : t('tasks.noData')}
                    </h3>
                    {hasActiveFilters && (
                        <button className="btn-secondary" onClick={handleClearFilters}>
                            {t('tasks.filters.clearAll')}
                        </button>
                    )}
                </div>
            ) : (
                <>
                    {/* Grid View */}
                    {viewMode === 'grid' && (
                        <div className="tasks-grid">
                            {tasks.map((task) => (
                                <TaskCard
                                    key={task._id}
                                    task={task}
                                    onToggleStatus={handleToggleStatus}
                                    onEdit={handleEdit}
                                />
                            ))}
                        </div>
                    )}

                    {/* List View */}
                    {viewMode === 'list' && (
                        <TasksList
                            tasks={tasks}
                            loading={loading}
                            onToggleStatus={handleToggleStatus}
                            onEdit={handleEdit}
                        />
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="pagination">
                            <button
                                className="pagination-btn"
                                disabled={currentPage === 1 || loading}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <span className="material-icons">chevron_left</span>
                                {t('pagination.previous')}
                            </button>
                            <span className="pagination-info">
                                {t('pagination.pageInfo', { current: currentPage, total: totalPages })}
                            </span>
                            <button
                                className="pagination-btn"
                                disabled={currentPage === totalPages || loading}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                {t('pagination.next')}
                                <span className="material-icons">chevron_right</span>
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Task Edit Modal */}
            {editingTask && (
                <TaskEditModal
                    task={editingTask}
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setEditingTask(null);
                    }}
                    onSave={handleSaveTask}
                />
            )}
        </div>
    );
};

export default TasksView;
